<script lang="ts">
// Evaluated once per module load, matching the previous withDefaults default. It lives in this
// block because prop defaults are hoisted outside setup() and cannot reference its locals.
const CURRENT_YEAR = new Date().getFullYear()
</script>

<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import { getBeltCertificateDisplayBaseImageUrl, getBeltCertificateShadowColorHex } from '@@/bits/belt_colors'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import { useSettingsBeltProgressEffectStore } from '@@/pinia/settings_belt_progress_effect'
import { useSettingsNavigationStore } from '@@/pinia/settings_navigation'
import { useSettingsUserInfoStore } from '@@/pinia/settings_user_info'
import DashboardSettingsBeltProgressBadgeContainer from '@@/vuecomponents/DashboardSettingsBeltProgressBadgeContainer.vue'
import DashboardSettingsBeltProgressBeltContainer from '@@/vuecomponents/DashboardSettingsBeltProgressBeltContainer.vue'
import DashboardSettingsBeltProgressCertificateBackgroundIllustration from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateBackgroundIllustration.vue'
import DashboardSettingsBeltProgressCertificateQRCodeContainer, {
  CertificateQRCodeSizePreset,
} from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateQRCodeContainer.vue'
import DashboardSettingsBeltProgressCertificateSealContainer, {
  CertificateSealSizePreset,
} from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateSealContainer.vue'
import DashboardSettingsBeltProgressCertificateSignature from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateSignature.vue'
import DashboardSettingsBeltProgressCertificateSubtitle, {
  CertificateSubtitleSizePreset,
} from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateSubtitle.vue'
import DashboardSettingsBeltProgressCertificateTitle from '@@/vuecomponents/DashboardSettingsBeltProgressCertificateTitle.vue'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const {
  signatureName = '',
  signatureRole = '',
  signatureStamp = '',
  certificateToken = '',
  certificateQrCode = '',
  beltColor = 'white',
  year = CURRENT_YEAR,
  withBackgroundIllustration = false,
  isDarkMode = false,
  maxScale = 1,
} = defineProps<{
  signatureName: string
  signatureRole: string
  signatureStamp: string
  certificateToken: string
  certificateQrCode: string
  beltColor: BeltColor
  year: number
  withBackgroundIllustration?: boolean
  isDarkMode?: boolean
  maxScale?: number
}>()
const settingsUserInfoStore = useSettingsUserInfoStore()
const settingsNavigationStore = useSettingsNavigationStore()
const { name, username, avatar } = storeToRefs(settingsUserInfoStore)

const userName = computed(() => {
  return name.value || username.value || ''
})

const xCertificate = computed<boolean>(() => beltColor !== 'white')

const containerRef = ref<HTMLElement | null>(null)
const certificateCardRef = ref<HTMLElement | null>(null)
const scale = ref(maxScale)

const CERTIFICATE_WIDTH = 740
const CERTIFICATE_HEIGHT = 450
const PADDING = 32 // 16px on each side

const updateScale = () => {
  if (!containerRef.value) return

  const container = containerRef.value
  const availableWidth = container.clientWidth - PADDING
  const availableHeight = container.clientHeight - PADDING

  const scaleX = availableWidth / CERTIFICATE_WIDTH
  const scaleY = availableHeight / CERTIFICATE_HEIGHT

  const newScale = Math.min(maxScale, scaleX, scaleY)
  // Fallback to maxScale if scale is invalid (e.g., container has no dimensions yet)
  scale.value = newScale > 0 ? newScale : maxScale
}

const noBeltProgressMessage = computed<string>(() =>
  __(
    `Earn your first belt by <a href="%{createPadletLink}" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">creating a padlet</a> and sharing it with others.`,
    {
      createPadletLink: settingsNavigationStore.getMakePageUrl(),
    },
  ),
)

const handleMessageClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.tagName.toLowerCase() === 'a') {
    event.preventDefault()
    settingsNavigationStore.goToMakePageFromSettings()
  }
}

let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      updateScale()
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

// #region Mouse tracking + Holographic effect
// Use shared effect store for mouse tracking
const effectStore = useSettingsBeltProgressEffectStore()
const { rx, ry, mx, my, glareOpacity } = storeToRefs(effectStore)

// Check if user prefers reduced motion for accessibility
const { isInReducedMotionMode } = useReducedMotionMode()

const handleMouseMove = (event: MouseEvent) => {
  // Disable 3D effect if user prefers reduced motion
  if (!certificateCardRef.value || beltColor === 'white' || isInReducedMotionMode.value) return
  effectStore.handleMouseMove(event, certificateCardRef.value)
}

const handleMouseEnter = () => {
  effectStore.handleMouseEnter()
}

const handleMouseLeave = () => {
  effectStore.handleMouseLeave()
}

/* 
  We want to scale the certificate according to the viewport size so that it displays correctly.
  On non-Safari browsers, we apply the 'scale' value using the 'zoom' property as it renders better.
  On Safari, 'zoom' renders weirdly so we use 'transform: scale()' instead.
*/
const certificateStyle = computed(() => {
  const baseStyle: Record<string, string | undefined> = {
    transform: `rotateX(${rx.value}deg) rotateY(${ry.value}deg)`,
    transformOrigin: 'center',
    '--mx': `${mx.value}%`,
    '--my': `${my.value}%`,
    '--o': glareOpacity.value.toString(),
  }

  if (device.safari) {
    const existingTransform = baseStyle.transform
    baseStyle.transform = `${existingTransform} scale(${scale.value})`
  } else {
    baseStyle.zoom = scale.value.toString()
  }

  return baseStyle
})

//#endregion
</script>

<template>
  <div
    ref="containerRef"
    dir="ltr"
    :role="xCertificate ? 'img' : undefined"
    :aria-label="xCertificate ? __('Current belt certificate') : undefined"
    :class="[
      !xCertificate ? 'w-full max-h-[240px]' : 'w-full h-full aspect-video',
      'relative overflow-hidden',
      {
        'select-none': xCertificate,
      },
    ]"
  >
    <DashboardSettingsBeltProgressCertificateBackgroundIllustration
      v-if="withBackgroundIllustration"
      role="presentation"
      :color="beltColor"
      :is-dark-mode="isDarkMode"
    />
    <div v-if="beltColor === 'white'" class="p-4 absolute inset-0 flex items-center justify-center">
      <p
        class="text-body-small font-semibold text-dark-text-100 dark:text-light-text-100 text-center text-balance"
        v-safe-html="noBeltProgressMessage"
        @click="handleMessageClick"
      ></p>
    </div>
    <div
      v-else
      class="absolute inset-0 flex items-center justify-center certificate-scene"
      @mousemove="handleMouseMove"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <!-- Scale the parent container so that the fixed size certificate scales as a group -->
      <div ref="certificateCardRef" class="certificate-3d-container rounded-lg" :style="certificateStyle">
        <div
          class="certificate-card relative flex justify-center rounded-lg overflow-hidden"
          :style="{
            width: `${CERTIFICATE_WIDTH}px`,
            height: `${CERTIFICATE_HEIGHT}px`,
            boxShadow: `0 24px 32px 0 ${getBeltCertificateShadowColorHex(beltColor)}`,
          }"
        >
          <ImageThumbnail
            :process-image="true"
            :width="CERTIFICATE_WIDTH"
            :height="CERTIFICATE_HEIGHT"
            :original-image-width="CERTIFICATE_WIDTH"
            :original-image-height="CERTIFICATE_HEIGHT"
            :src="getBeltCertificateDisplayBaseImageUrl(beltColor)"
            alt=""
            class="w-full h-full object-cover"
          />
          <!-- goodcheck-disable-next-line -->
          <div class="absolute left-1/2 -translate-x-1/2 top-[80px]">
            <DashboardSettingsBeltProgressBadgeContainer :color="beltColor" :year="year">
              <DashboardSettingsBeltProgressBeltContainer
                :color="beltColor"
                :is-dark-mode="isDarkMode"
                :stroke-size="8"
                :progress="1"
                :size="76"
                data-testid="beltDisplay"
              >
                <!-- Avatar -->
                <OzAvatar :src="avatar" :width="64" :height="64" class="rounded-full object-cover" />
              </DashboardSettingsBeltProgressBeltContainer>
            </DashboardSettingsBeltProgressBadgeContainer>
          </div>
          <div class="absolute top-1/2 translate-y-[calc(-50%+31px)] start-0 end-0 flex justify-center px-4">
            <DashboardSettingsBeltProgressCertificateTitle :name="userName" :belt-color="beltColor" />
          </div>
          <div class="absolute bottom-5 flex flex-col items-center gap-y-5">
            <DashboardSettingsBeltProgressCertificateSignature
              :is-black-belt="beltColor === 'black'"
              :signature-name="signatureName"
              :signature-role="signatureRole"
              :signature-stamp="signatureStamp"
              :year="year"
            />
            <DashboardSettingsBeltProgressCertificateSubtitle
              :color="beltColor"
              :size-preset="CertificateSubtitleSizePreset.Display"
              class="px-28 py-1.5"
            />
          </div>
        </div>
        <!-- z-20 keeps the seal above the glare effect -->
        <div class="absolute bottom-3 start-4 z-20">
          <DashboardSettingsBeltProgressCertificateSealContainer
            :token="certificateToken"
            :size-preset="CertificateSealSizePreset.Display"
          />
        </div>
        <div class="absolute bottom-5.5 end-7">
          <DashboardSettingsBeltProgressCertificateQRCodeContainer
            :certificate-token="certificateToken"
            :certificate-qr-code="certificateQrCode"
            :size-preset="CertificateQRCodeSizePreset.Display"
            :belt-color="beltColor"
          />
        </div>
        <div class="certificate-glare" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.certificate-scene {
  perspective: 1000px;
  perspective-origin: center;
}

.certificate-3d-container {
  transition: transform 0.1s ease-out;
  transform-style: preserve-3d;
  will-change: transform;
  /* Performance optimizations for 3D transforms */
  backface-visibility: hidden; /* Hide back face during 3D rotation */
  -webkit-backface-visibility: hidden; /* Safari support */
  transform-style: preserve-3d;
}

.certificate-glare {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 10;
  overflow: hidden;
  border-radius: inherit;
  /* The radial gradient creates the shine, with its position controlled by --mx and --my. */
  background: radial-gradient(
    farthest-corner circle at var(--mx) var(--my),
    hsla(0, 0%, 100%, 0.8) 10%,
    hsla(0, 0%, 100%, 0.65) 20%,
    hsla(0, 0%, 0%, 0.5) 90%
  );
  mix-blend-mode: overlay;
  /* The opacity is controlled by the --o variable, making it visible on hover. */
  opacity: calc(var(--o) * 0.66);
  transition: opacity 0.3s ease-out;
  /* Performance optimizations for GPU acceleration */
  contain: layout style paint; /* Isolate rendering scope */
  transform: translateZ(0); /* Force GPU compositing layer */
  will-change: opacity; /* Hint browser for opacity changes */
}
</style>
